import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COLOR_LIST, OPTION_COLORS } from "@/components/badges";
import { Plus, X, GripVertical, Settings2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

const STAGE_KEYS = ["asama1", "asama2", "asama3", "asama4"];
const GROUP_EMOJI = { asama1: "📞", asama2: "🤝", asama3: "📄", asama4: "🧪", sonuc: "🏆" };
const fmt = (n) => Number(n || 0).toLocaleString("tr-TR");
const COLOR_TEXT = {
  slate: "text-slate-500",
  blue: "text-blue-500",
  green: "text-emerald-500",
  rose: "text-rose-500",
  amber: "text-amber-500",
  violet: "text-violet-500",
  teal: "text-teal-500",
};

function groupSum(records, key, sumField) {
  const map = {};
  records.forEach((r) => {
    const g = (r.values?.[key] ?? "").toString().trim() || "—";
    if (!map[g]) map[g] = { count: 0, sum: 0 };
    map[g].count += 1;
    map[g].sum += parseFloat(r.values?.[sumField]) || 0;
  });
  return Object.entries(map)
    .map(([k, v]) => ({ key: k, ...v }))
    .sort((a, b) => b.sum - a.sum || b.count - a.count);
}

function Metric({ count, label, active, primary, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex flex-col items-center justify-center px-3 py-1 min-w-[76px] rounded-md transition-colors ${
        active ? "bg-primary/15 ring-1 ring-primary" : "hover:bg-secondary"
      }`}
    >
      <div className={`text-xl font-bold leading-none ${primary || active ? "text-primary" : "text-foreground"}`}>
        {fmt(count)}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap">{label}</div>
    </button>
  );
}

function Group({ title, emoji, children }) {
  return (
    <div className="flex flex-col border-r border-border last:border-0 px-2">
      <div className="text-[11px] font-semibold text-muted-foreground text-center mb-1 whitespace-nowrap">
        {emoji} {title}
      </div>
      <div className="flex items-stretch">{children}</div>
    </div>
  );
}

function AddReportDialog({ open, onOpenChange, columns, onAdd }) {
  const [title, setTitle] = useState("");
  const [groupBy, setGroupBy] = useState("");
  const [metric, setMetric] = useState("count");
  const [sumField, setSumField] = useState("");
  const [color, setColor] = useState("blue");
  const numCols = columns.filter((c) => c.type === "number");

  const save = () => {
    if (!groupBy) return;
    const gb = columns.find((c) => c.key === groupBy);
    onAdd({
      title: title.trim() || `${gb?.label}`,
      group_by: groupBy,
      metric,
      sum_field: metric === "sum" ? sumField : null,
      color,
    });
    setTitle(""); setGroupBy(""); setMetric("count"); setSumField(""); setColor("blue");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-report-dialog">
        <DialogHeader>
          <DialogTitle>Üst Rapora Sayaç Ekle</DialogTitle>
          <DialogDescription>Bir sütuna göre gruplanmış sayaç/tutar raporu oluşturun.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Başlık (opsiyonel)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Örn. Temsilci" data-testid="report-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Neye Göre Grupla</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="report-groupby-select"><SelectValue placeholder="Sütun seçiniz" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {columns.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ölçüt</Label>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger data-testid="report-metric-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">Adet</SelectItem>
                  <SelectItem value="sum">Toplam</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Renk</Label>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger data-testid="report-color-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLOR_LIST.map((c) => (
                    <SelectItem key={c} value={c}>
                      <span className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${OPTION_COLORS[c].split(" ")[0]}`} />
                        {c}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {metric === "sum" && (
            <div className="space-y-1.5">
              <Label>Toplanacak Alan</Label>
              <Select value={sumField} onValueChange={setSumField}>
                <SelectTrigger data-testid="report-sumfield-select"><SelectValue placeholder="Sayısal sütun" /></SelectTrigger>
                <SelectContent>
                  {numCols.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button onClick={save} disabled={!groupBy || (metric === "sum" && !sumField)} data-testid="report-save-button">Ekle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TopReportBar({
  records,
  columns,
  filters,
  asamasizOnly,
  onMetric,
  reports = [],
  onAddReport,
  onRemoveReport,
  onReorderReports,
  hiddenGroups = [],
  onToggleGroups,
  isAdmin,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const dragIdx = useRef(null);
  const colOf = (k) => columns.find((c) => c.key === k);
  const total = records.length;
  const cnt = (key, val) => records.filter((r) => (r.values?.[key] ?? "") === val).length;

  const segCol = colOf("segment");
  const asamasiz = records.filter((r) => STAGE_KEYS.every((k) => !(r.values?.[k] ?? "").toString().trim())).length;
  const stageCols = STAGE_KEYS.map(colOf).filter((c) => c && (c.options || []).length);
  const sonucCol = colOf("sonuc");
  const noFilter = Object.values(filters).every((v) => !v) && !asamasizOnly;
  const colActive = (key, val) => filters[key] === val && !asamasizOnly;

  const hidden = (key) => hiddenGroups.includes(key);
  const toggleGroup = (key) => {
    const next = hidden(key) ? hiddenGroups.filter((k) => k !== key) : [...hiddenGroups, key];
    onToggleGroups?.(next);
  };
  const groupDefs = [
    { key: "genel", label: "GENEL" },
    ...stageCols.map((c) => ({ key: c.key, label: c.label })),
    ...(sonucCol ? [{ key: "sonuc", label: sonucCol.label || "Sonuç" }] : []),
  ];

  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from == null || from === i) return;
    const next = [...reports];
    const [m] = next.splice(from, 1);
    next.splice(i, 0, m);
    dragIdx.current = null;
    onReorderReports?.(next);
  };

  return (
    <Card className="flex-shrink-0 p-2" data-testid="top-report-bar">
      <div className="flex flex-wrap items-stretch gap-x-1 gap-y-2">
        {!hidden("genel") && (
        <Group title="GENEL" emoji="📊">
          <Metric count={total} label="Toplam" primary active={noFilter} onClick={() => onMetric({ mode: "all" })} testid="metric-toplam" />
          {segCol && (segCol.options || []).map((o) => (
            <Metric key={o.value} count={cnt("segment", o.value)} label={o.value} active={colActive("segment", o.value)} onClick={() => onMetric({ mode: "col", key: "segment", value: o.value })} testid={`metric-segment-${o.value}`} />
          ))}
          <Metric count={asamasiz} label="Aşamasız" active={asamasizOnly} onClick={() => onMetric({ mode: "asamasiz" })} testid="metric-asamasiz" />
        </Group>
        )}

        {stageCols.filter((col) => !hidden(col.key)).map((col) => (
          <Group key={col.key} title={col.label} emoji={GROUP_EMOJI[col.key] || "📌"}>
            {(col.options || []).map((o) => (
              <Metric key={o.value} count={cnt(col.key, o.value)} label={o.value} active={colActive(col.key, o.value)} onClick={() => onMetric({ mode: "col", key: col.key, value: o.value })} testid={`metric-${col.key}-${o.value}`} />
            ))}
          </Group>
        ))}

        {sonucCol && !hidden("sonuc") && (
          <Group title={sonucCol.label || "SONUÇ"} emoji={GROUP_EMOJI.sonuc}>
            {(sonucCol.options || []).map((o) => (
              <Metric key={o.value} count={cnt("sonuc", o.value)} label={o.value} active={colActive("sonuc", o.value)} onClick={() => onMetric({ mode: "col", key: "sonuc", value: o.value })} testid={`metric-sonuc-${o.value}`} />
            ))}
          </Group>
        )}

        {/* Custom draggable report widgets */}
        {reports.map((rep, i) => {
          const rows = groupSum(records, rep.group_by, rep.sum_field);
          const ct = COLOR_TEXT[rep.color] || "text-foreground";
          const dot = (OPTION_COLORS[rep.color] || OPTION_COLORS.slate).split(" ")[0];
          return (
            <div
              key={rep.id}
              draggable
              onDragStart={() => (dragIdx.current = i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="flex flex-col border-r border-border last:border-0 px-2 group cursor-grab"
              data-testid={`custom-report-${rep.id}`}
            >
              <div className="flex items-center justify-center gap-1 text-[11px] font-semibold mb-1 whitespace-nowrap">
                <GripVertical className="w-3 h-3 opacity-0 group-hover:opacity-60" />
                <span className={`w-2 h-2 rounded-full ${dot}`} />
                <span className={ct}>{rep.title}</span>
                {isAdmin && (
                  <button onClick={() => onRemoveReport?.(rep.id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-report-${rep.id}`}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="flex items-stretch">
                {rows.slice(0, 12).map((row) => (
                  <div key={row.key} className="flex flex-col items-center justify-center px-3 py-1 min-w-[72px]">
                    <div className={`text-xl font-bold leading-none ${ct}`}>
                      {rep.metric === "sum" ? `₺${fmt(row.sum)}` : fmt(row.count)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap">{row.key}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {onAddReport && isAdmin && (
          <div className="flex items-center gap-1 px-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="topbar-settings-button">
                  <Settings2 className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56" data-testid="topbar-settings-popover">
                <div className="text-sm font-semibold mb-2">Üst Rapor Görünürlüğü</div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {groupDefs.map((g) => (
                    <div key={g.key} className="flex items-center justify-between">
                      <span className="text-sm">{g.label}</span>
                      <Switch
                        checked={!hidden(g.key)}
                        onCheckedChange={() => toggleGroup(g.key)}
                        data-testid={`toggle-group-${g.key}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Kapatılan grup verisi olsa da gizlenir.</p>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-report-button">
              <Plus className="w-4 h-4 mr-1" /> Rapor
            </Button>
          </div>
        )}
      </div>
      <AddReportDialog open={addOpen} onOpenChange={setAddOpen} columns={columns} onAdd={onAddReport} />
    </Card>
  );
}
